/**
 * Sync Status Component
 *
 * Displays background sync status and queued requests
 * Shows notification when offline with pending requests
 */

import { useEffect, useState } from 'react';
import { Wifi, WifiOff, RefreshCw, CheckCircle, AlertCircle } from 'lucide-react';
import { useBackgroundSync } from '../hooks/useBackgroundSync';
import { useBadging } from '../hooks/usePWAFeatures';

export function SyncStatus() {
  const { isOnline, isSyncing, queueCount, sync } = useBackgroundSync();
  const { updateBadge } = useBadging();
  const [showStatus, setShowStatus] = useState(false);
  const [lastNotification, setLastNotification] = useState<'success' | 'error' | null>(null);

  // Only show status when there are queued items (don't rely on navigator.onLine)
  // navigator.onLine is unreliable on mobile, so we only show when queue has items
  useEffect(() => {
    setShowStatus(queueCount > 0);
  }, [queueCount]);

  // Update app badge with queue count
  useEffect(() => {
    updateBadge(queueCount);
  }, [queueCount, updateBadge]);

  // Auto-hide success notification
  useEffect(() => {
    if (lastNotification === 'success') {
      const timer = setTimeout(() => setLastNotification(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [lastNotification]);

  const handleSync = async () => {
    try {
      await sync();
      setLastNotification('success');
    } catch (error) {
      setLastNotification('error');
    }
  };

  if (!showStatus && !lastNotification) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 z-40 max-w-sm">
      {/* Offline Status */}
      {!isOnline && (
        <div className="bg-amber-500/95 backdrop-blur-sm text-white rounded-lg shadow-lg p-4 mb-2">
          <div className="flex items-center gap-3">
            <WifiOff className="w-5 h-5 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-semibold">You're Offline</p>
              {queueCount > 0 && (
                <p className="text-sm text-amber-50">
                  {queueCount} {queueCount === 1 ? 'request' : 'requests'} queued
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Online with Queued Requests */}
      {isOnline && queueCount > 0 && (
        <div className="bg-blue-500/95 backdrop-blur-sm text-white rounded-lg shadow-lg p-4 mb-2">
          <div className="flex items-center gap-3">
            <Wifi className="w-5 h-5 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-semibold">Connection Restored</p>
              <p className="text-sm text-blue-50">
                {queueCount} {queueCount === 1 ? 'request' : 'requests'} pending
              </p>
            </div>
            <button
              onClick={handleSync}
              disabled={isSyncing}
              className="bg-white/20 hover:bg-white/30 disabled:bg-white/10 px-3 py-2 rounded-lg transition-colors flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} />
              {isSyncing ? 'Syncing...' : 'Sync Now'}
            </button>
          </div>
        </div>
      )}

      {/* Syncing Status */}
      {isSyncing && (
        <div className="bg-blue-500/95 backdrop-blur-sm text-white rounded-lg shadow-lg p-4 mb-2">
          <div className="flex items-center gap-3">
            <RefreshCw className="w-5 h-5 flex-shrink-0 animate-spin" />
            <div className="flex-1">
              <p className="font-semibold">Syncing Requests...</p>
              <p className="text-sm text-blue-50">Please wait</p>
            </div>
          </div>
        </div>
      )}

      {/* Success Notification */}
      {lastNotification === 'success' && (
        <div className="bg-green-500/95 backdrop-blur-sm text-white rounded-lg shadow-lg p-4 mb-2 animate-slide-up">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-5 h-5 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-semibold">Sync Complete</p>
              <p className="text-sm text-green-50">All requests synced successfully</p>
            </div>
          </div>
        </div>
      )}

      {/* Error Notification */}
      {lastNotification === 'error' && (
        <div className="bg-red-500/95 backdrop-blur-sm text-white rounded-lg shadow-lg p-4 mb-2">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-semibold">Sync Failed</p>
              <p className="text-sm text-red-50">Some requests could not be synced</p>
            </div>
            <button
              onClick={handleSync}
              className="bg-white/20 hover:bg-white/30 px-3 py-2 rounded-lg transition-colors text-sm"
            >
              Retry
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
